// Migration Path for Dynamic Model Loading
//
// Models are now fetched once on app load in MainViewScreen.js and stored in global state.
// Components can access them via AppStateManager state or the re-exported arrays.
//
// For direct state access:
// const { state } = AppStateManager.useAppStateManager();
// const { baseModels, functionCallingModels, visionModels } = state;

import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import { useState, useEffect } from "react";

/**
 * Hook that returns models from AppStateManager state
 * This ensures models are only fetched once at app load
 * @returns {Object} Models data and loading state
 */
export const useModels = () => {
  const { state } = AppStateManager.useAppStateManager();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (
      state?.baseModels?.length > 0 ||
      state?.functionCallingModels?.length > 0 ||
      state?.visionModels?.length > 0
    ) {
      setIsLoading(false);
    }
  }, [state?.baseModels, state?.functionCallingModels, state?.visionModels]);

  return {
    baseModels: state?.baseModels || [],
    functionCallingModels: state?.functionCallingModels || [],
    visionModels: state?.visionModels || [],
    isLoading,
  };
};

export const useExternalModels = () => {
  const { state } = AppStateManager.useAppStateManager();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (state?.externalModels?.length > 0) {
      setIsLoading(false);
    }
  }, [state?.externalModels]);

  return {
    externalModels: state?.externalModels || [],
    externalDeprecatedModels: state?.externalDeprecatedModels || [],
    externalProviders: state?.externalProviders || [],
    isLoading,
  };
};

export const useRegionalModelIds = () => {
  const { state } = AppStateManager.useAppStateManager();
  return state?.regionalModelIds || [];
};

/**
 * Function to set models in global state (used by MainViewScreen.js)
 * @param {Function} dispatch - AppStateManager dispatch function
 * @param {Object} models - Models object to set
 */
export const setModelsInState = (dispatch, models) => {
  dispatch("SET_MODELS", {
    functionCallingModels: models.functionCallingModels || [],
    baseModels: models.baseModels || [],
    visionModels: models.visionModels || [],
  });
};

// Re-export the helper for direct API access if needed
export { default as modelsHelper } from "../../API/Models/modelsHelper";

// ================== BACKWARD COMPATIBILITY EXPORTS ==================
// These exports will be replaced at runtime by the actual models from AppStateManager
// For now they return empty arrays but will be populated via useModels() hook
//
// For proper dynamic loading, components should:
// 1. Import useModels from this file
// 2. Call useModels() within the component
// 3. Use the returned arrays
export let _functionCallingModels = [];
export let _baseModels = [];
export let visionModels = [];
